import { Text } from '@fluentui/react-components';
import { ClockRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../Communication/CommunicationMessage';
import { WidgetsHeadline } from '../WidgetsHeadline';
import { SubstitutionDaySection } from './SubstitutionDaySection';
import { SubstitutionWidgetProps } from './SubstitutionWidget.interface';
import './SubstitutionWidget.scss';
import { formatLastUpdated } from './SubstitutionWidget.utils';
import { SubstitutionWidgetSkeleton } from './SubstitutionWidgetSkeleton';

export const SubstitutionWidget: FC<SubstitutionWidgetProps> = ({
  isLoading,
  days,
  lastUpdated,
  error,
  onRetry,
  classNames,
  useDarkIllustration = false,
}) => {
  const { t } = useTranslation();

  const hasSubstitutions = days.some((day) => day.entries.length > 0);

  return (
    <div
      data-cy="substitutionWidget"
      className={cn('substitutionWidget flex flex-col', classNames)}
    >
      <WidgetsHeadline
        content={t('dashboard:substitution:header', { defaultValue: 'Vertretungsplan' })}
        icon={<ClockRegular />}
      />

      {lastUpdated && (
        <Text className="substitutionWidget__lastUpdated" size={200}>
          {t('dashboard:substitution:last_updated', {
            defaultValue: 'Letzte Aktualisierung: {{date}}',
            date: formatLastUpdated(lastUpdated),
          })}
        </Text>
      )}

      {isLoading && <SubstitutionWidgetSkeleton />}

      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center gap-3 substitutionWidget__error">
          <Text className="text-error">
            {t('dashboard:substitution:load_error', {
              defaultValue: 'Fehler beim Laden des Vertretungsplans',
            })}
          </Text>
          {onRetry && (
            <button onClick={onRetry} className="substitutionWidget__retryButton">
              {t('dashboard:substitution:retry', { defaultValue: 'Neu laden' })}
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && (
        <div className="substitutionWidget__content">
          {!hasSubstitutions && (
            <CommunicationMessage
              illustration={Illustrations.Empty}
              headline=""
              text={t('dashboard:substitution:empty', {
                defaultValue: 'Keine Vertretungen vorhanden',
              })}
              illustrationSize={IllustrationSize.Small}
              useDarkIllustration={useDarkIllustration}
            />
          )}

          {hasSubstitutions && (
            <div
              className={cn('substitutionWidget__days', {
                'substitutionWidget__days--multiDay': days.length > 1,
              })}
            >
              {days.map((day) => (
                <SubstitutionDaySection key={day.date.toISOString()} day={day} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
